import { getDefaultBuConfig } from 'domain/repository/apiUtil';
import _ from 'lodash';
import { AutoCompleteChangeParams /*, AutoCompleteSelectParams*/ } from 'primereact/autocomplete';
import { VirtualScrollerLazyParams } from 'primereact/virtualscroller';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { DynamicProp } from 'veronica-ui-component/dist/common/commonInterface';
import { Icons } from 'veronica-ui-component/dist/common/Icon';
import { HPHSkyBlue } from 'veronica-ui-component/dist/component/core/Colors';
import { IconButton } from 'veronica-ui-component/dist/component/core/IconButtons/IconButtons';
import { Lozenges } from 'veronica-ui-component/dist/component/core/Lozenges/Lozenges';
import {
    AutoCompleteGlobalStyles, StyledAutoComplete, StyledInputErrorMsg, StyledInputLabel,
    StyledInputWrapper,
    StyledLozengesContainer,
    StyledMenuItemTemplate, StyledMenuItemTemplateIcon,
    StyledMenuItemTemplateValue,
    StyledOptional,
    StyledRequired,
} from './styled/NbisInputDropDown.styled';

// Define types for options and props
export interface DropdownOption {
    dropdownLabel?: string;
    value?: string | number;
    tagLabel?: string | number;
    icon?: string;
    isMaster?: boolean;
    lozengesLabel?: string;
    lozengesVariation?: string;
    showText?: string;
    [key: string]: any;
}

interface VirtualScrollerOptions extends DynamicProp {
    showLoader?: boolean;
    delay?: number;
    lazy?: boolean;
    onLazyLoad?(e: VirtualScrollerLazyParams): void;
}

export interface InputDropdownProps extends DynamicProp {
    id?: string;
    options?: DropdownOption[];
    freeTextInput?: boolean;
    mode?: 'single' | 'multiple';
    virtualScrollerOptions?: VirtualScrollerOptions;
    onChange?(value: DropdownOption | DropdownOption[] | null): void;
    onDropdownClick?(): void;
    inputType?: 'number' | 'text' | 'freeText';
    field?: string;
    width?: string;
    arrowBackground?: 'transparent' | 'white';
    disableInput?: boolean;
    sort?: boolean;
    scrollHeight?: string;
    isVirtualScroll?: boolean;
    label?: string;
    required?: boolean;
    optional?: boolean;
    errorMessage?: string;
    helpIcon?: boolean;
    toolTipText?: string;
    placeholder?: string;
    value?: DropdownOption | DropdownOption[] | string | null;
    isLimitOptions?: boolean;
    isCustomBlur?: boolean;
    isSelFirstWhenEmpty?: boolean
}

const NbisInputDropDown = React.forwardRef((props: InputDropdownProps, ref?: React.Ref<any>) => {
    const {
        id = 'inputDropdownId',
        options = [],
        freeTextInput = false,
        mode = 'single',
        virtualScrollerOptions = {
            delay: 0,
            lazy: true,
            showLoader: true
        },
        onChange,
        onDropdownClick,
        inputType = 'freeText',
        field = 'dropdownLabel',
        width = '250px',
        arrowBackground = 'white',
        disableInput = false,
        sort = true,
        scrollHeight,
        isVirtualScroll = true,
        label = 'Label',
        required = false,
        optional = false,
        errorMessage = '',
        helpIcon = false,
        toolTipText = 'Helper Message',
        placeholder = '',
        value: propsValue = null,
        isLimitOptions = true,
        isCustomBlur = false,
        isSelFirstWhenEmpty = false,
        ...rest
    } = props;
    const [selectedValue, setSelectedValue] = useState<any>(null);
    const [filteredList, setFilteredList] = useState<DropdownOption[]>([]);
    const [panelLeft, setPanelLeft] = useState<number | null>(null);
    const [tempValue, setTempValue] = useState<string>('');
    const elemRef = useRef<any>();
    const inputLabel = useRef<HTMLLabelElement>(null);
    const [propOptions, setPropOptions] = useState<DropdownOption[]>([]);
    const defaultDropdownLimmit: Number = getDefaultBuConfig<Number>("REACT_APP_DROPDOWN_LIMIT_COUNT") || 0;
    const [optsLimitCount] = useState<number>(defaultDropdownLimmit ? Number(defaultDropdownLimmit) : 0);
    const selectedItemChangeRef = useRef<boolean>(false);
    // Add a ref to store the current input value
    const currentInputValueRef = useRef<string>('');

    useImperativeHandle(ref, () => ({
        inputLabel: inputLabel,
    }));

    // Add a function to retrieve the input text value
    const getInputText = useCallback(() => {
        if (elemRef.current && elemRef.current.inputRef && elemRef.current.inputRef.current) {
            return elemRef.current.inputRef.current.value || '';
        }
        return '';
    }, []);

    // Find matching options
    const findMatchingOptions = useCallback((inputText: string): DropdownOption[] => {
        if (!inputText || inputText.trim() === '') {
            return [];
        }

        const searchText = inputText.trim();
        let sortedOptions = sort ? [...options]?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!)) : [...options];

        // Use the same rating and sorting logic as searchList
        const scoredOptions = sortedOptions?.map(option => {
            const label = String(option.value || option.dropdownLabel || '');
            const lowerLabel = label.toLowerCase();

            let score = 0;
            if (lowerLabel.startsWith(searchText.toLowerCase())) {
                score += 2;
            } else if (lowerLabel.includes(searchText.toLowerCase())) {
                score += 1;
            }

            return { option, score, label };
        }).filter(item => item.score > 0).sort((a, b) => {
            return b.score - a.score ||
                a.label.localeCompare(b.label);
        });

        // Get options
        let matchedOptions = scoredOptions.map(item => item.option);

        // Apply the same quantity limit as searchList
        if (isLimitOptions && matchedOptions && matchedOptions.length > 0) {
            matchedOptions = matchedOptions.slice(0, optsLimitCount);
        }

        return matchedOptions;
    }, [isLimitOptions, options, optsLimitCount, sort]);

    const searchList = useCallback(({ query }: { query: string }) => {
        let sortedOptions = sort ? [...options]?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!)) : [...options];

        let filteredOpts: DropdownOption[] = [];
        if (query === '' && tempValue === '') {
            //setFilteredList([...sortedOptions]);
            filteredOpts = [...sortedOptions];
        } else if (query !== '') {
            const searchText = query;
            // Save the current input value
            currentInputValueRef.current = searchText;

            const tmpfilteredList = sortedOptions?.map(option => {
                const label = String(option.dropdownLabel || option.value || '');
                const lowerLabel = label.toLowerCase();

                let score = 0;
                if (lowerLabel.startsWith(searchText.toLowerCase())) {
                    score += 2;
                } else if (lowerLabel.includes(searchText.toLowerCase())) {
                    score += 1;
                }

                return { option, score, label };

            }).filter(item => item.score > 0).sort((a, b) => {
                return b.score - a.score ||
                    a.label.localeCompare(b.label);
            }).map(item => ({
                ...item.option,
                showText: (item.option?.dropdownLabel || item.option?.value)?.toString().replace(new RegExp(`(${searchText})`, 'gi'), match => `<span class="highlight">${match}</span>`)
            }));
            setTempValue(searchText);
            //setFilteredList(tmpfilteredList);
            filteredOpts = tmpfilteredList;
        } else {
            //setFilteredList([...filteredList]);
            filteredOpts = [...filteredList];
        }

        let limitOpts: DropdownOption[] = [];
        if (isLimitOptions && filteredOpts && filteredOpts.length > 0) {
            limitOpts = filteredOpts.slice(0, optsLimitCount);
        } else {
            limitOpts = filteredOpts;
        }
        setFilteredList(limitOpts);
    }, [sort, options, tempValue, isLimitOptions, filteredList, optsLimitCount]);

    const isSelected = useCallback((item: DropdownOption, selectedValue: DropdownOption | DropdownOption[] | null, mode: 'single' | 'multiple') => {
        if (mode === 'multiple' && _.isArray(selectedValue)) {
            return selectedValue?.some(i => i.value === item.value);
        }
        return selectedValue && !_.isArray(selectedValue) && selectedValue?.value === item.value;
    }, []);

    const escapeHtml = useCallback((text: string) => {
        return text.replace(/[<>"&]/g, m => ({
            '<': '&lt;', '>': '&gt;',
            '&': '&amp;', '"': '&quot;'
        }[m]!));
    }, []);

    const getDisplayText = useCallback((item: DropdownOption, isItemSelected: boolean) => {
        if (!item) return { __html: '' };
        if (item.showText) {
            return { __html: item.showText };
        }
        let displayText = (item.dropdownLabel || item.value || '').toString();
        displayText = escapeHtml(displayText);
        return isItemSelected ? { __html: `<span class="highlight">${displayText}</span>` } : { __html: `${displayText}` };
    }, [escapeHtml]);

    const handleOptionMouseDown = useCallback((option: DropdownOption) => {
        selectedItemChangeRef.current = true;
    }, []);

    const itemTemplate = useCallback((item: DropdownOption) => {
        if (!item) return null;

        const isItemSelected = item ? isSelected(item, selectedValue, mode) : false;
        const displayText = getDisplayText(item, isItemSelected ?? false);

        return (
            <StyledMenuItemTemplate className={isItemSelected ? 'dropdownSelected' : ''}
                onMouseDown={(e) => {
                    handleOptionMouseDown(item);
                }}>
                {item.icon && (
                    <StyledMenuItemTemplateIcon className={isItemSelected ? 'iconSelected' : ''}>
                        {Icons(item.icon, isItemSelected ? HPHSkyBlue : undefined)}
                    </StyledMenuItemTemplateIcon>
                )}
                <StyledMenuItemTemplateValue dangerouslySetInnerHTML={displayText} />
                {item.isMaster && (
                    <StyledLozengesContainer id="LozengesContainer">
                        <Lozenges label={item.lozengesLabel} variation={item.lozengesVariation} />
                    </StyledLozengesContainer>
                )}
            </StyledMenuItemTemplate>
        );
    }, [isSelected, selectedValue, mode, getDisplayText, handleOptionMouseDown]);

    const selectedMultipleItemTemplate = useCallback((item: DropdownOption) => (
        <StyledMenuItemTemplate>
            {item.icon && <StyledMenuItemTemplateIcon className="icon">{Icons(item.icon)}</StyledMenuItemTemplateIcon>}
            <StyledMenuItemTemplateValue>{item[field || 'value'] || item.value}</StyledMenuItemTemplateValue>
        </StyledMenuItemTemplate>
    ), [field]);

    const isNumber = useCallback((evt: React.KeyboardEvent<HTMLInputElement>) => evt.which > 31 && (evt.which < 48 || evt.which > 57) ? false : true, []);

    const isAlfa = useCallback((evt: React.KeyboardEvent<HTMLInputElement>) => evt.which > 31 && (evt.which < 65 || evt.which > 90) && (evt.which < 97 || evt.which > 122) ? false : true, []);

    const handleFreeText = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (freeTextInput && e.which === 13 && tempValue) {
            const itemExists = propOptions?.find(item => item.value === tempValue || item.dropdownLabel === tempValue);
            if (!itemExists) {
                setFilteredList(prevFilteredList => [...prevFilteredList, { value: tempValue }]);
                const newValue = mode === 'multiple' ? [...(Array.isArray(selectedValue) ? selectedValue : []), { value: tempValue }] : tempValue;
                setSelectedValue(newValue as DropdownOption | DropdownOption[] | null);
                setTempValue('');
                if (mode === 'multiple') {
                    elemRef.current.inputRef.current.value = '';
                }
                onChange && onChange(newValue as DropdownOption | DropdownOption[] | null);
            }
        }
    }, [freeTextInput, tempValue, propOptions, mode, selectedValue, onChange]);

    const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        const shouldType = inputType === 'number' ? isNumber(e) : inputType === 'text' ? isAlfa(e) : true;
        shouldType ? handleFreeText(e) : e.preventDefault();
    }, [inputType, isNumber, isAlfa, handleFreeText]);

    /*const selectFirstItem = useCallback(() => {
        if (!filteredList?.length && !isSelFirstWhenEmpty) return;

        if (mode === 'single' && !freeTextInput) {
            let tempSelValue: DropdownOption | null = null;
            if (filteredList?.length) {
                tempSelValue = filteredList[0];
            } else if (isSelFirstWhenEmpty && options?.length > 0) {
                tempSelValue = options[0];
            }
            
            setSelectedValue(tempSelValue);
            onChange && onChange(tempSelValue);
            if (elemRef?.current?.inputRef?.current) {
                elemRef.current.inputRef.current.value = '';
            }            
        } else if (mode === 'multiple' && !freeTextInput) {
            if (filteredList?.length) {
                const newSelectedValue = _.isEmpty(selectedValue) ?
                    [filteredList[0]] :
                    [...selectedValue, filteredList[0]];
                setSelectedValue(newSelectedValue);
                onChange && onChange(newSelectedValue);
                if (elemRef?.current?.inputRef?.current) {
                    elemRef.current.inputRef.current.value = '';
                }
            }
        }
    }, [filteredList, isSelFirstWhenEmpty, mode, freeTextInput, onChange, options, selectedValue]);*/

    const handleBlur = useCallback(() => {
        // Use a flag to check if user just clicked on an option
        if (selectedItemChangeRef.current) {
            selectedItemChangeRef.current = false;
            return;
        }

        // Get current input text
        const inputText = getInputText();
        const hasInputText = inputText && inputText.trim() !== '';

        // Case 1: No input text
        if (!hasInputText) {
            // When isSelFirstWhenEmpty is enabled in single mode with no free text input
            if (isSelFirstWhenEmpty && mode === 'single' && !freeTextInput && options?.length > 0) {
                // Select first option (original functionality)
                // Get first option according to sort rules
                let firstOption = options[0];
                if (sort) {
                    const sortedOptions = [...options]?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!));
                    firstOption = sortedOptions[0];
                }
                setSelectedValue(firstOption);
                onChange && onChange(firstOption);
            }
            // Clear filtered list
            setFilteredList([]);
            return;
        }

        // Case 2: Has input text
        // Find matching options (using same logic as search)
        const matchingOptions = findMatchingOptions(inputText);

        if (matchingOptions.length > 0) {
            // Has matching options, select the first one
            const firstMatch = matchingOptions[0];

            if (mode === 'single') {
                // Single mode: set selected value to first match
                setSelectedValue(firstMatch);
                onChange && onChange(firstMatch);
            } else if (mode === 'multiple') {
                // Multiple mode: clear input field first
                if (elemRef?.current?.inputRef?.current) {
                    elemRef.current.inputRef.current.value = '';
                }
                // Check if already selected
                const isAlreadySelected = _.some(selectedValue,
                    (item: DropdownOption) => _.isEqual(item.value, firstMatch.value));

                if (!isAlreadySelected) {
                    // Add to selected values
                    const newSelectedValue = _.isEmpty(selectedValue) ?
                        [firstMatch] :
                        [...selectedValue, firstMatch];
                    setSelectedValue(newSelectedValue);
                    onChange && onChange(newSelectedValue);
                }
            }
        } else {
            // No matching options found
            if (freeTextInput) {
                // If free text input is allowed, add as new item
                const newItem = {
                    value: inputText,
                    dropdownLabel: inputText,
                    tagLabel: inputText
                };

                if (mode === 'single') {
                    // Single mode: set to new item
                    setSelectedValue(newItem);
                    onChange && onChange(newItem);
                } else if (mode === 'multiple') {
                    // Multiple mode: add to selected values
                    const newSelectedValue = _.isEmpty(selectedValue) ?
                        [newItem] :
                        [...selectedValue, newItem];
                    setSelectedValue(newSelectedValue);
                    onChange && onChange(newSelectedValue);
                }
            } else {
                // Non-free text input mode
                // Clear input field
                if (elemRef?.current?.inputRef?.current) {
                    elemRef.current.inputRef.current.value = '';
                }
                // Clear selected value for single mode               

                if (mode === 'single') {
                    // Single mode: set to new item
                    setSelectedValue(null);
                    onChange && onChange(null);
                }
            }
        }

        // Clear temporary value
        setTempValue('');
    }, [findMatchingOptions, freeTextInput, getInputText, isSelFirstWhenEmpty, mode, onChange, options, selectedValue, sort]);

    const onDropdownClickInternal = useCallback(() => {
        const pAuto = elemRef.current.container;
        const rect = pAuto.getBoundingClientRect();
        setPanelLeft(rect.left);
        onDropdownClick && onDropdownClick();
        if (elemRef.current && disableInput) {
            elemRef.current.inputRef.current.blur();
        }
        setTempValue('');
    }, [onDropdownClick, disableInput]);

    useEffect(() => {
        let currentSelectedValue: any = propsValue;
        let sortedOptions = propOptions?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!));
        if (typeof propsValue === 'string' && propsValue.trim()) {
            const filteredList = sortedOptions?.filter(item => (item.value || item.dropdownLabel)?.toString().toLowerCase() === propsValue.toLowerCase());
            currentSelectedValue = filteredList[0] || currentSelectedValue;
        } else if (Array.isArray(propsValue)) {
            currentSelectedValue = propsValue;
        } else {
            currentSelectedValue = propsValue && typeof propsValue === 'object' ? propsValue : currentSelectedValue;
        }
        setSelectedValue(currentSelectedValue);
    }, [propOptions, propsValue, setSelectedValue]);

    useEffect(() => {
        let tempOptions: DropdownOption[] = [];
        if (isLimitOptions && options && options.length > 0) {
            tempOptions = options.slice(0, optsLimitCount);
        } else {
            tempOptions = options;
        }

        tempOptions = tempOptions?.filter(a => !(a?.dropdownLabel === null || a?.dropdownLabel === undefined || a?.dropdownLabel === ""));

        if (JSON.stringify(propOptions) !== JSON.stringify(tempOptions?.sort((a, b) => a?.dropdownLabel!.localeCompare(b?.dropdownLabel!)))) {
            setPropOptions(tempOptions);
        }
    }, [isLimitOptions, options, optsLimitCount, propOptions])

    const handleOnChange = useCallback((e: AutoCompleteChangeParams) => {
        if (mode === 'single' && !e.value) {
            setFilteredList([]);
        }
        if (e.value && _.isArray(e.value) && !_.isEmpty(e.value) && e.value.length > selectedValue?.length) {
            const value = e.value[e.value.length - 1].value;

            if (_.some(selectedValue, (item: DropdownOption) => _.isEqual(item.value, value))) {
                return;
            }
        }

        setSelectedValue(e.value === "" ? null : e.value);
        setTempValue('');
        onChange && onChange(e.value === "" ? null : e.value);
    }, [mode, onChange, selectedValue]);

    /*const handleSelect = useCallback((e: AutoCompleteSelectParams) => {
        selectedItemChangeRef.current = true;
    }, []);*/

    let inputWidth, panelWidth, autoCompleteWidth = width;
    if (width && !isNaN(Number(width.replace('px', '').replace('%', '')))) {
        const w = Number(width.replace('px', '').replace('%', ''));
        panelWidth = `${w - 2}px`;
        // autoCompleteWidth = `${w - 45}px`;
        inputWidth = Array.isArray(selectedValue) && selectedValue.length ? (w * .8) : '100%';
    }

    return <>
        <AutoCompleteGlobalStyles panelLeft={panelLeft} />
        <StyledInputWrapper onMouseLeave={() => document.body.click()} className={`${(!freeTextInput && disableInput && mode === 'multiple') ? 'inputDisable' : ''}`}>
            <StyledInputLabel ref={inputLabel}>
                {label} {required && !optional && <StyledRequired>*</StyledRequired>}
                {optional && !required && <StyledOptional><Lozenges variation={'Optional Label'} /></StyledOptional>}
                {helpIcon && <IconButton fileName={'Icon-help'} size={'small'} idIcon={`Icon-help-${id}`} toolTipPlacement={'right'} toolTipArrow={false} disabled={false} toolTipText={toolTipText} {...rest} />}
            </StyledInputLabel>
            <StyledAutoComplete
                {...rest}
                arrowBackground={arrowBackground}
                autoCompleteWidth={autoCompleteWidth}
                ref={elemRef}
                autoHighlight={false}
                appendTo="self"
                errorMessage={!!errorMessage}
                {...freeTextInput ? { onKeyPress: handleKeyPress, dropdownIcon: 'Icon-auto-text' } : {}}
                onKeyPress={handleKeyPress}
                value={selectedValue}
                suggestions={filteredList}
                completeMethod={searchList}
                field={field || 'value'}
                onDropdownClick={onDropdownClickInternal}
                dropdown
                {...mode === 'multiple' ?
                    { multiple: true, selectedItemTemplate: selectedMultipleItemTemplate, removeIcon: 'pi pi-times' } :
                    {}}
                {...isVirtualScroll ? {
                    virtualScrollerOptions: {
                        ...virtualScrollerOptions,
                        ...!virtualScrollerOptions?.itemSize ? { itemSize: 30 } : {}, style: { width: panelWidth },
                    }
                } : {}}
                scrollHeight={scrollHeight === '' || scrollHeight === undefined ? (propOptions.length < 7 ? `${options.length * 30}px ` : '225px') : scrollHeight}
                style={{ width }}
                placeholder={placeholder}
                itemTemplate={itemTemplate}
                inputStyle={{ width: inputWidth }}
                readOnly={!freeTextInput && disableInput}
                onChange={handleOnChange}
                // onHide={handleOnHide}
                {...(!isCustomBlur ? { onBlur: handleBlur } : {})}
            //onSelect={handleSelect}
            />
            <StyledInputErrorMsg>{errorMessage}</StyledInputErrorMsg>
        </StyledInputWrapper>
    </>

});

export default NbisInputDropDown;